import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Title,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  Card,
  RingProgress,
  Tabs,
  Divider,
  Button,
  ActionIcon,
  Loader,
  Alert,
  Progress,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { BackButton } from '../../components/ui/BackButton';
import {
  IconUser,
  IconMail,
  IconTrophy,
  IconTarget,
  IconEdit,
  IconSettings,
  IconBook,
} from '@tabler/icons-react';
import { UserCompetency, Team, Enrollment, Course } from '../../types';
import { ProfileEditModal } from '../../components/user/ProfileEditModal';
import { UserProfileCompetencies } from '../../components/user/UserProfileCompetencies';
import { GoalsTab } from '../../components/goals/GoalsTab';
import { DevelopmentPlanTab } from '../../components/goals/DevelopmentPlanTab';
import { useHasFeature } from '../../store/featureFlagsStore';
import { useCompanyModules } from '../../hooks/useCompanyModules';
import { RoleDistributionSettings } from '../../components/profile/RoleDistributionSettings';
import { useUserStore } from '../../store/userStore';
import { api, apiFormData, ApiError } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getMonthsInTeam } from '../../utils/date';
import { resolveGradeDisplay, CompetencyMapLike } from '../../utils/gradeDisplay';
import { IconCheck, IconAlertCircle, IconUserCheck, IconStairsUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useUserCompetencyMap,
  useUserGoals,
  useMyEnrollments,
  useUserEnrollments,
} from '../../hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDevice } from '../../hooks/useDevice';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { usePermission } from '../../hooks/usePermission';

/**
 * Универсальный компонент профиля пользователя
 * Работает как для собственного профиля (/profile), так и для профилей других пользователей (/profile/:id)
 * Поддерживает роутинг для вкладок: /profile/competencies, /profile/goals, /profile/:id/competencies, /profile/:id/goals
 */
export const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useUserStore((s) => s.user);
  const canOpenTeams = usePermission('company.structure');
  const { t } = useTranslation();
  const deviceType = useDevice();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [editModalOpened, setEditModalOpened] = useState(false);

  // Определяем, является ли это собственным профилем
  const isOwnProfile = !id || currentUser?._id === id;

  // Определяем активную вкладку из URL
  useEffect(() => {
    const path = location.pathname;
    // План развития — `?tab=plan`: отдельного роута у вкладки нет, а `/profile/plan`
    // совпал бы с `/profile/:id`.
    if (new URLSearchParams(location.search).get('tab') === 'plan') {
      setActiveTab('plan');
    } else if (path.includes('/competencies')) {
      setActiveTab('competencies');
    } else if (path.includes('/goals')) {
      setActiveTab('goals');
    } else if (path.includes('/learning')) {
      setActiveTab('learning');
    } else {
      setActiveTab('overview');
    }
  }, [location.pathname, location.search, isOwnProfile]);

  /**
   * Функция для обновления роута при смене вкладки
   * Позволяет пользователям делиться прямыми ссылками на конкретные вкладки
   *
   * @param value - значение вкладки ('overview', 'competencies', 'goals')
   */
  const handleTabChange = (value: string | null) => {
    if (!value) return;

    setActiveTab(value);

    if (value === 'overview') {
      // Для overview не меняем роут - остается базовый профиль
      if (id) {
        navigate(`/profile/${id}`);
      } else {
        navigate('/profile');
      }
    } else if (value === 'competencies') {
      // Для competencies обновляем роут на /competencies
      if (id) {
        navigate(`/profile/${id}/competencies`);
      } else {
        navigate('/profile/competencies');
      }
    } else if (value === 'goals') {
      // Для goals обновляем роут на /goals
      if (id) {
        navigate(`/profile/${id}/goals`);
      } else {
        navigate('/profile/goals');
      }
    } else if (value === 'learning') {
      if (id) {
        navigate(`/profile/${id}/learning`);
      } else {
        navigate('/profile/learning');
      }
    } else if (value === 'plan') {
      navigate(id ? `/profile/${id}?tab=plan` : '/profile?tab=plan');
    }
  };

  // Track profile view on load
  const hasTrackedProfile = useRef(false);

  useEffect(() => {
    hasTrackedProfile.current = false;
  }, [id]);

  useEffect(() => {
    if (!hasTrackedProfile.current && currentUser?._id) {
      track(AnalyticsEvents.USER_PROFILE_VIEWED, {
        userId: id || currentUser._id,
        isSelf: isOwnProfile,
      });
      hasTrackedProfile.current = true;
    }
  }, [id, currentUser?._id, isOwnProfile, track]);

  // Получаем данные пользователя
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    queryKey: ['user', id || 'me'],
    queryFn: async () => {
      if (id) {
        // Загружаем профиль другого пользователя
        console.log('Fetching user with ID:', id);
        const result = await api(`/users/${id}`);
        console.log('User data received:', result);
        return result;
      } else {
        // Загружаем собственный профиль
        console.log('Fetching own profile');
        const result = await api('/users/me');
        console.log('Own profile data received:', result);
        return result;
      }
    },
    enabled: true,
    retry: 1,
    retryDelay: 1000,
  });

  // Карта компетенций того, чей профиль открыт, а не смотрящего.
  const { data: competencyMap, isLoading: isMapLoading } = useUserCompetencyMap(
    isOwnProfile ? 'me' : id,
  );

  // Разделы, не подключённые компании, — без вкладок и запросов (`Tenant.modules`).
  const { hasModule } = useCompanyModules();
  const hasCompetencies = hasModule('competencyMap');
  // Цели пользователя. Цели коллеги видят только он сам, админ, его лид/PM и саммарайзер —
  // остальным бэкенд отдаёт 403, поэтому вкладку и счётчик им не показываем.
  const { data: userGoals = [], error: goalsError } = useUserGoals(
    user?._id || '',
    hasModule('goals'),
  );
  const canViewGoals =
    hasModule('goals') && !(goalsError instanceof ApiError && goalsError.status === 403);
  // Обучение коллеги — у кого есть learning.progress на него (руководитель, админ); остальным
  // бэкенд отвечает 403, и вкладки нет, как у целей.
  const { data: theirEnrollments = [], isSuccess: canViewLearning } = useUserEnrollments(
    user?._id || '',
    hasModule('learning') && !isOwnProfile,
  );
  const hasLearning = hasModule('learning') && (isOwnProfile || canViewLearning);
  // План развития — бета: цели под компетенции следующего грейда.
  const isPlanOn = useHasFeature('developmentPlan') && canViewGoals;

  // Получаем записи на курсы
  const { data: myEnrollments = [] } = useMyEnrollments();
  const enrollments = isOwnProfile ? myEnrollments : theirEnrollments;

  // Вычисляем количество активных целей
  const activeGoalsCount = React.useMemo(() => {
    return userGoals.filter((goal) => goal.status === 'pending' || goal.status === 'accepted')
      .length;
  }, [userGoals]);

  // Прогресс считается так же, как на дашборде и в плане развития: компетенции следующего
  // грейда (на верхнем — текущего). Раньше здесь под «Прогресс до Senior» считались
  // компетенции текущего грейда, и профиль спорил с дашбордом: 4 из 6 против 0 из 6.
  const userProgress = React.useMemo(() => {
    if (!user?.grade || !competencyMap) return null;
    const display = resolveGradeDisplay(
      competencyMap as unknown as CompetencyMapLike,
      (user.competencies || []).map((uc: UserCompetency) => uc.competencyId),
      user.grade._id,
    );
    if (!display.currentGrade) return null;
    return {
      currentGrade: user.grade,
      nextGrade: display.nextGrade,
      totalCompetenciesInGrade: display.totalCount,
      acquiredCompetenciesInGrade: display.acquiredCount,
      remainingCompetencies: display.totalCount - display.acquiredCount,
      progressPercent: display.progressPct,
    };
  }, [user, competencyMap]);

  const handleSaveProfile = async (formData: FormData) => {
    try {
      const endpoint = isOwnProfile ? '/users/me' : `/users/${id}`;
      await apiFormData(endpoint, {
        method: 'PATCH',
        body: formData,
      });

      // Инвалидируем query для перезагрузки данных
      queryClient.invalidateQueries({ queryKey: ['user', id || 'me'] });
      // Своё имя и фото видны и в сайдбаре, и на дашборде — они берут их из стора.
      if (isOwnProfile) void useUserStore.getState().checkSession();

      showToast({
        title: t('profile.profileUpdated'),
        message: t('profile.profileUpdatedMsg'),
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
    } catch (e: unknown) {
      showToast({
        title: t('common.error'),
        message: e instanceof Error ? e.message : t('profile.profileUpdateError'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      throw e;
    }
  };

  if (isUserLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </PageContainer>
    );
  }

  if (userError || !user) {
    return (
      <PageContainer>
        <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
          <Text>
            {userError
              ? `${t('profile.loadingError')}: ${userError instanceof Error ? userError.message : t('common.unknownError')}`
              : t('profile.userNotFound')}
          </Text>
          <BackButton />
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Заголовок страницы */}
      <Group justify="space-between" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <Group gap="md">
          <BackButton />
          <Title order={2}>
            {isOwnProfile
              ? t('profile.title')
              : t('profile.profileOf', { name: user?.name || '—' })}
          </Title>
        </Group>
        <Group gap={deviceType === 'mobile' ? 'xs' : 'sm'}>
          {isOwnProfile && (
            <>
              {deviceType === 'mobile' ? (
                <>
                  <ActionIcon
                    variant="light"
                    color="teal"
                    size="md"
                    onClick={() => setEditModalOpened(true)}
                    title={t('common.edit')}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="gray"
                    size="md"
                    onClick={() => navigate('/settings')}
                    title={t('navigation.settings')}
                  >
                    <IconSettings size={16} />
                  </ActionIcon>
                </>
              ) : (
                <>
                  <Button
                    variant="light"
                    color="teal"
                    leftSection={<IconEdit size={16} />}
                    size="sm"
                    onClick={() => setEditModalOpened(true)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconSettings size={16} />}
                    size="sm"
                    color="gray"
                    onClick={() => navigate('/settings')}
                  >
                    {t('navigation.settings')}
                  </Button>
                </>
              )}
            </>
          )}
        </Group>
      </Group>

      {/* Вкладка без флага плана или неподключённого раздела — обзор, а не пустая вкладка.
          Цели без доступа — вкладка с «Доступ запрещен». */}
      <Tabs
        value={
          (activeTab === 'plan' && !isPlanOn) ||
          (activeTab === 'competencies' && !hasCompetencies) ||
          (activeTab === 'goals' && !hasModule('goals')) ||
          (activeTab === 'learning' && !hasLearning)
            ? 'overview'
            : activeTab
        }
        onChange={handleTabChange}
        color="teal"
      >
        <Tabs.List mb="xl">
          <Tabs.Tab value="overview" leftSection={<IconUser size={16} />}>
            {t('profile.overview')}
          </Tabs.Tab>
          {hasCompetencies && (
            <Tabs.Tab value="competencies" leftSection={<IconTrophy size={16} />}>
              {isOwnProfile ? t('profile.myCompetencies') : t('profile.competencies.tabLabel')}
            </Tabs.Tab>
          )}
          {canViewGoals && (
            <Tabs.Tab value="goals" leftSection={<IconTarget size={16} />}>
              {t('profile.goalsTitle')}
            </Tabs.Tab>
          )}
          {isPlanOn && (
            <Tabs.Tab value="plan" leftSection={<IconStairsUp size={16} />}>
              {t('profile.plan.tab')}
            </Tabs.Tab>
          )}
          {hasLearning && (
            <Tabs.Tab value="learning" leftSection={<IconBook size={16} />}>
              {t('learning.title')}
            </Tabs.Tab>
          )}
          {isOwnProfile && (
            <Tabs.Tab value="roles" leftSection={<IconUserCheck size={16} />}>
              {t('profile.roles')}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="overview" style={{ transition: 'opacity 0.3s ease-in-out' }}>
          <Group gap={deviceType === 'mobile' ? 'md' : 'xl'} align="flex-start" wrap="wrap">
            {/* Основная информация */}
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ flex: deviceType === 'mobile' ? 'none' : 1 }}
              w={deviceType === 'mobile' ? '100%' : 'auto'}
            >
              <Group gap="lg" align="flex-start" wrap="nowrap">
                <Avatar src={user?.avatar || undefined} size={80} radius="xl" />
                <Stack gap="xs" style={{ minWidth: 0 }}>
                  <Title order={2}>{user?.name || '—'}</Title>
                  {(user?.position || user?.profession) && (
                    <Text size="sm" c="dimmed">
                      {user.position || user.profession?.name}
                      {user.position && user.profession && ` | ${user.profession.name}`}
                    </Text>
                  )}

                  <Group gap="lg" mt="md" wrap="wrap">
                    <Group gap="xs" wrap="nowrap" miw={0}>
                      <IconMail size={16} color="var(--mantine-color-gray-6)" />
                      <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
                        {user?.email || '—'}
                      </Text>
                    </Group>
                    {/* <Group gap="xs">
                      <IconCalendar size={16} color="var(--mantine-color-gray-6)" />
                      <Text size="sm">{t('profile.inTeamSince', { date: '30 ноября 2024г' })}</Text>
                    </Group> */}
                  </Group>
                </Stack>
              </Group>
            </Card>

            {/* Прогресс по грейдам */}
            {hasCompetencies && (
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{ flex: deviceType === 'mobile' ? 'none' : 1 }}
                w={deviceType === 'mobile' ? '100%' : 'auto'}
              >
                <Stack gap="md">
                  <Title order={3}>{t('profile.progressTitle')}</Title>

                  <Group gap="lg" wrap="wrap">
                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        {t('profile.currentGrade')}
                      </Text>
                      <Badge color="teal" size="lg" variant="light">
                        {userProgress?.currentGrade?.title || user?.grade?.title || '—'}
                      </Badge>
                    </div>

                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        {t('profile.nextGrade')}
                      </Text>
                      <Badge
                        color={userProgress?.nextGrade ? 'rgb(225, 192, 3)' : 'gray'}
                        size="lg"
                        variant="light"
                      >
                        {userProgress?.nextGrade?.title || t('profile.noNextGrade')}
                      </Badge>
                    </div>
                  </Group>

                  {userProgress ? (
                    <div>
                      <Text size="sm" c="dimmed" mb={8}>
                        {userProgress.nextGrade
                          ? t('profile.progressTo', { grade: userProgress.nextGrade.title })
                          : t('profile.progressInCurrentGrade')}
                      </Text>
                      <Group gap="md" align="center" wrap="wrap">
                        <RingProgress
                          size={80}
                          thickness={8}
                          sections={[{ value: userProgress.progressPercent, color: 'teal' }]}
                          label={
                            <Text ta="center" size="xs" fw={700}>
                              {userProgress.progressPercent}%
                            </Text>
                          }
                        />
                        <div>
                          <Text size="sm" fw={500}>
                            {t('profile.competenciesProgress', {
                              acquired: userProgress.acquiredCompetenciesInGrade,
                              total: userProgress.totalCompetenciesInGrade,
                            })}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {userProgress.remainingCompetencies > 0
                              ? t('profile.remainingCompetencies', {
                                  count: userProgress.remainingCompetencies,
                                })
                              : t('profile.gradeCompleted')}
                          </Text>
                        </div>
                      </Group>
                    </div>
                  ) : (
                    <div>
                      <Text size="sm" c="dimmed" mb={8}>
                        {t('profile.progressUnavailable')}
                      </Text>
                      <Group gap="md" align="center" wrap="wrap">
                        <RingProgress
                          size={80}
                          thickness={8}
                          sections={[{ value: 0, color: 'gray' }]}
                          label={
                            <Text ta="center" size="xs" fw={700}>
                              —
                            </Text>
                          }
                        />
                        <div>
                          <Text size="sm" fw={500} c="dimmed">
                            {t('profile.noProgressData')}
                          </Text>
                        </div>
                      </Group>
                    </div>
                  )}
                </Stack>
              </Card>
            )}
          </Group>

          {/* Команды пользователя */}
          {user.teams && user.teams.length > 0 && (
            <Card shadow="sm" padding="lg" radius="md" mt="xl" withBorder>
              <Title order={3} mb="md">
                {t('profile.teams')}
              </Title>
              <Stack gap="sm">
                {user.teams.map((team: Team) => (
                  <Group key={team._id} justify="space-between">
                    <Group gap="sm">
                      <IconUser size={16} color="var(--mantine-color-gray-6)" />
                      <Text>{team.name}</Text>
                      <Badge color={team.status === 'active' ? 'teal' : 'gray'} variant="light">
                        {team.status === 'active'
                          ? t('profile.teamActive')
                          : t('profile.teamInactive')}
                      </Badge>
                    </Group>
                    {/* /teams — по праву company.structure, остальным кнопка дала бы 403 */}
                    {canOpenTeams && (
                      <Button variant="light" size="xs" onClick={() => navigate(`/teams`)}>
                        {t('profile.goToTeam')}
                      </Button>
                    )}
                  </Group>
                ))}
              </Stack>
            </Card>
          )}

          {/* Разделитель перед статистикой */}
          <Divider my="xl" />

          {/* Блок статистики - показывает ключевые метрики пользователя */}
          <Title order={3} mb="lg">
            {t('profile.stats')}
          </Title>
          <Group gap="md">
            {hasCompetencies && (
              <Card shadow="sm" padding="md" radius="md" withBorder>
                <Text size="lg" fw={700} c="teal">
                  {user?.competencies?.length || 0}
                </Text>
                <Text size="sm" c="dimmed">
                  {t('profile.competenciesCount')}
                </Text>
              </Card>
            )}

            {canViewGoals && (
              <Card shadow="sm" padding="md" radius="md" withBorder>
                <Text size="lg" fw={700} c="blue">
                  {activeGoalsCount}
                </Text>
                <Text size="sm" c="dimmed">
                  {t('profile.activeGoals')}
                </Text>
              </Card>
            )}

            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Text size="lg" fw={700} c="green">
                {getMonthsInTeam(user?.hireDate) ?? '—'}
              </Text>
              <Text size="sm" c="dimmed">
                {t('profile.monthsInTeam')}
              </Text>
            </Card>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="competencies" style={{ transition: 'opacity 0.3s ease-in-out' }}>
          {user && competencyMap && (
            <UserProfileCompetencies
              user={user}
              competencyMap={competencyMap}
              isMapLoading={isMapLoading}
              isOwnProfile={isOwnProfile}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="goals" style={{ transition: 'opacity 0.3s ease-in-out' }}>
          {canViewGoals ? (
            <GoalsTab userId={user?._id || ''} />
          ) : (
            <Alert icon={<IconAlertCircle size={16} />} color="gray">
              {t('errors.accessDenied')}
            </Alert>
          )}
        </Tabs.Panel>

        {isPlanOn && user && (
          <Tabs.Panel value="plan">
            {competencyMap ? (
              <DevelopmentPlanTab
                user={user}
                competencyMap={competencyMap}
                isOwnProfile={isOwnProfile}
              />
            ) : isMapLoading ? (
              <Group justify="center" py="xl">
                <Loader size="sm" />
              </Group>
            ) : (
              // Без профессии или карты следующего грейда нет — так и говорим, а не пустота.
              <Text c="dimmed" size="sm">
                {t('profile.plan.noNext')}
              </Text>
            )}
          </Tabs.Panel>
        )}

        <Tabs.Panel value="learning" style={{ transition: 'opacity 0.3s ease-in-out' }}>
          {enrollments.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              {t(isOwnProfile ? 'learning.noEnrollments' : 'learning.noEnrollmentsOther')}
            </Text>
          ) : (
            <Stack gap="md">
              {/* Completed courses */}
              {enrollments
                .filter((e: Enrollment) => e.status === 'completed')
                .map((e: Enrollment) => {
                  const courseData = typeof e.courseId === 'string' ? null : (e.courseId as Course);
                  return (
                    <Group key={e._id} gap="sm">
                      <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                        {courseData?.title || t('learning.title')}
                      </Badge>
                      {e.completedAt && (
                        <Text size="xs" c="dimmed">
                          {new Date(e.completedAt).toLocaleDateString()}
                        </Text>
                      )}
                    </Group>
                  );
                })}

              {/* In-progress courses */}
              {enrollments
                .filter((e: Enrollment) => e.status !== 'completed')
                .map((e: Enrollment) => {
                  const courseData = typeof e.courseId === 'string' ? null : (e.courseId as Course);
                  return (
                    <Card key={e._id} shadow="sm" padding="sm" radius="md" withBorder>
                      <Group justify="space-between" mb={4}>
                        <Text size="sm" fw={500}>
                          {courseData?.title || t('learning.title')}
                        </Text>
                        {courseData?.type !== 'scorm' && (
                          <Text size="xs" c="dimmed">
                            {Math.round(e.progress)}%
                          </Text>
                        )}
                      </Group>
                      {courseData?.type !== 'scorm' && <Progress value={e.progress} size="sm" />}
                    </Card>
                  );
                })}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="roles" style={{ transition: 'opacity 0.3s ease-in-out' }}>
          {isOwnProfile && <RoleDistributionSettings />}
        </Tabs.Panel>
      </Tabs>

      {/* Модальное окно редактирования профиля */}
      {isOwnProfile && (
        <ProfileEditModal
          opened={editModalOpened}
          onClose={() => setEditModalOpened(false)}
          onSave={handleSaveProfile}
          userData={user}
        />
      )}
    </PageContainer>
  );
};
